/* QR Share — opt-in: saves a photo-free copy of the card on the server so a
   printed QR code can reopen it on any phone. */
import React from "react";
import { QrCode, RefreshCw, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function QrShareCard({ share, shareUrl, qrDataUrl, busy, onCreate, onSync, onRevoke }) {
  return (
    <div
      data-testid="qr-share-card"
      className="bg-white border border-[#E5E2DC] rounded-2xl p-6 print:hidden"
    >
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2 inline-flex items-center gap-1.5">
        <QrCode size={11} /> QR code — reopen this card on any phone
      </div>
      {!share?.card_id ? (
        <>
          <p className="text-sm text-[#5C6B62] leading-relaxed max-w-2xl">
            Add a QR code to your printed cards. Anyone who scans it — you, family, a caregiver,
            or ER staff — instantly opens this medication card on their phone. Creating a QR code
            saves a copy of this card (names, doses, and directions — <span className="font-semibold text-[#1C2B23]">never your photos</span>)
            on our secure server so the link works anywhere.
          </p>
          <button
            data-testid="create-qr-code"
            type="button"
            disabled={busy}
            onClick={onCreate}
            className="mt-4 inline-flex items-center gap-2 bg-[#2B5339] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#1C2B23] disabled:opacity-50"
          >
            <QrCode size={15} /> {busy ? "Creating…" : "Create my QR code"}
          </button>
        </>
      ) : (
        <div className="flex flex-wrap items-start gap-5">
          {qrDataUrl && (
            <img
              data-testid="qr-image"
              src={qrDataUrl}
              alt="QR code that opens this medication card"
              className="w-32 h-32 border border-[#E5E2DC] rounded-lg"
            />
          )}
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm text-[#1C2B23]">
              Your QR code is live — it now prints automatically on your master card, wallet card,
              and appointment copy. It updates itself whenever you change a medication.
            </p>
            <div className="mt-2 text-xs text-[#5C6B62] break-all">{shareUrl}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                data-testid="copy-qr-link"
                type="button"
                onClick={() => {
                  navigator.clipboard
                    .writeText(shareUrl)
                    .then(() => toast.success("Card link copied."))
                    .catch(() => toast.message(shareUrl));
                }}
                className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#E5E2DC] rounded-full px-3 py-1.5 hover:border-[#2B5339] text-[#5C6B62]"
              >
                <Copy size={12} /> Copy link
              </button>
              <button
                data-testid="sync-qr-code"
                type="button"
                disabled={busy}
                onClick={onSync}
                className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#E5E2DC] rounded-full px-3 py-1.5 hover:border-[#2B5339] text-[#5C6B62] disabled:opacity-50"
              >
                <RefreshCw size={12} /> Update QR data now
              </button>
              <button
                data-testid="revoke-qr-code"
                type="button"
                disabled={busy}
                onClick={onRevoke}
                className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#E5E2DC] rounded-full px-3 py-1.5 hover:border-[#C26747] hover:text-[#C26747] text-[#5C6B62] disabled:opacity-50"
              >
                <Trash2 size={12} /> Turn off &amp; delete from server
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
